<template>
  <div class="panel" v-if="cmSectionState !== 'empty'">
    <div class="cm-sec-head">
      <h2>{{ $t('CM_TIMELINES') }}</h2>
      <p class="cm-hint">{{ $t('CM_TIMELINES_HINT') }}</p>
      <div class="settings">
        <div class="cm-seg">
          <button
            v-for="metric in metrics"
            :key="metric.key"
            :class="{ active: activeMetric === metric.key }"
            @click="setMetric(metric.key)"
          >
            {{ $t(metric.label) }}
          </button>
        </div>
        <div class="period-btns" v-click-outside="closeGranularity">
          <span
            class="cm-select-label"
            :class="{ active: granularityOpen }"
            @click="granularityOpen = !granularityOpen"
          >
            {{ $t(currentGranularityLabel) }}
            <span class="expand"></span>
          </span>
          <ul v-show="granularityOpen">
            <li
              v-for="g in granularities"
              :key="g.key"
              :class="{ active: granularity === g.key }"
              @click="setGranularity(g.key)"
            >
              {{ $t(g.label) }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="loading" class="cm-state">{{ $t('CM_LOADING') }}</div>
    <div v-else-if="error" class="cm-state is-error">{{ error }}</div>

    <template v-else>
      <!-- легенда: ★-слова впереди и подсвечены -->
      <div class="cm-chips">
        <span
          v-for="(t, i) in terms"
          :key="t.term"
          :class="['cm-chip', { 'is-tracked': t.added, 'is-off': hidden.has(t.term) }]"
          @click="toggle(t.term)"
        >
          <span class="cm-dot" :style="{ background: colorOf(i) }"></span>
          <template v-if="t.added">★</template>{{ t.term }}
          <span
            class="cm-term"
            :title="$t('CM_TL_ISOLATE')"
            @click.stop="isolate(t.term)"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          </span>
          <span
            class="cm-term is-open"
            :title="$t('CM_TL_OPEN')"
            @click.stop="openTerm(t.term)"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"/><path d="m21 3-9 9"/><path d="M15 3h6v6"/></svg>
          </span>
        </span>
      </div>

      <p v-if="activeMetric === 'rel_tr' && !hasTrData" class="cm-hint">{{ $t('CM_TR_NO_SIGNAL') }}</p>
      <div v-else class="m-chart">
        <div ref="metricChart" class="chart"></div>
      </div>
    </template>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { debounce, merge } from "lodash";
import moment from "moment";
import comOpt from "@/models/charts/common-options.json";
import { enableTouchTooltip } from "@/models/charts/echarts-touch";
import cmScopeMixin from "@/mixins/cmScopeMixin";
import cmSectionMixin from "@/mixins/cmSectionMixin";
import cmApi, {
  aggregateSeries,
  bucketsBetween,
  defaultGranularity,
} from "@/api/controllers/content-monitoring";
import bus from "@/models/content-monitoring/bus";
import ClickOutside from "vue-click-outside";

export default {
  name: "Cm-timelines",
  mixins: [cmScopeMixin, cmSectionMixin],
  directives: { ClickOutside },

  data() {
    return {
      // сырой дневной ряд из API; неделя/месяц считаются из него на клиенте
      rawTerms: [],
      hidden: new Set(),
      granularity: "day",
      granularityOpen: false,
      activeMetric: "mentions",
      loading: false,
      error: "",
      charts: [],
      granularities: [
        { key: "day", label: "DASH_DAILY" },
        { key: "week", label: "DASH_WEEKLY" },
        { key: "month", label: "DASH_MONTHLY" },
      ],
    };
  },

  computed: {
    /** Есть ли что показывать: хотя бы один термин с рядом. */
    hasData() {
      return this.terms.length > 0;
    },

    /**
     * Есть ли вообще обсуждаемость в выборке. Считаем по СЫРОМУ дневному ряду:
     * относит. TR приходит NULL по страницам без комментарийного фона (порог —
     * в top_discussion.sql/timeline_series.sql), и если NULL везде, график TR
     * рисовать нечем. Ноль — это данные (пост без комментариев), null — нет.
     */
    hasTrData() {
      return this.rawTerms.some((t) => (t.series || []).some((p) => p.rel_tr != null));
    },

    periodKey() {
      const period = this.getPeriod || {};
      return `${period.start}..${period.end}`;
    },

    palette() {
      const c = this.$colors;
      return [c.blue, c.viol, c.red, c.green, c.orange, c.pink, c.yellow];
    },

    currentGranularityLabel() {
      const found = this.granularities.find((g) => g.key === this.granularity);
      return found ? found.label : "";
    },

    metrics() {
      return [
        { key: "mentions", label: "CM_TL_TAB_MENTIONS" },
        { key: "rel_er", label: "CM_TL_TAB_ER" },
        { key: "rel_tr", label: "CM_TL_TAB_TR" },
      ];
    },

    /**
     * ★-слова вперёд, чтобы они были видны по умолчанию.
     * Отсеиваем термы без данных: отслеживаемые слова приходят из CM всегда,
     * даже если за период по ним нет ни одного упоминания — рисовать по ним
     * пустые линии (и пустой график) не нужно.
     */
    terms() {
      return [...this.rawTerms]
        .filter((t) => this.hasSeriesData(t))
        .sort((a, b) => (b.added === true) - (a.added === true))
        .map((t) => ({
          ...t,
          series: aggregateSeries(t.series, this.granularity),
        }));
    },

    /**
     * Ось X — ВСЕ корзины периода подряд, а не только даты с данными. API отдаёт
     * лишь дни с упоминаниями; ось из них прятала пропуски, и слово с одним
     * упоминанием в январе и одним в августе рисовалось ровной линией «1» через
     * весь год. Даты из рядов добавляем на случай, если период задан неточно.
     */
    axisDates() {
      const period = this.getPeriod || {};
      const all = new Set(bucketsBetween(period.start, period.end, this.granularity));
      this.terms.forEach((t) => t.series.forEach((p) => all.add(p.date)));
      // даты формата YYYY-MM-DD / YYYY-MM — лексикографический порядок верный
      return [...all].sort();
    },

    dates() {
      const fmt = this.granularity === "month" ? "MMM YYYY" : "DD MMM";
      return this.axisDates.map((date) => moment(date).format(fmt));
    },
  },

  watch: {
    // период сменился — выбираем грануляцию заново (ручной выбор живёт до этого)
    periodKey: {
      immediate: true,
      handler() {
        const period = this.getPeriod || {};
        this.granularity = defaultGranularity(period.start, period.end);
      },
    },
    // график TR появился/исчез — состав инстансов другой, пересобираем
    hasTrData() {
      if (!this.hasTrData && this.activeMetric === "rel_tr") {
        this.activeMetric = "rel_er";
      }
      this.disposeCharts();
      this.$nextTick(this.renderCharts);
    },
  },

  methods: {
    colorOf(i) {
      return this.palette[i % this.palette.length];
    },

    /** У терма есть данные, если в ряду встречается хотя бы одно упоминание. */
    hasSeriesData(t) {
      return Array.isArray(t.series) && t.series.some((p) => (p.mentions || 0) > 0);
    },

    /** Перезапрос не нужен: API отдаёт дневной ряд, агрегируем локально. */
    closeGranularity() {
      this.granularityOpen = false;
    },

    setGranularity(key) {
      this.granularityOpen = false;
      if (this.granularity === key) return;
      this.granularity = key;
      this.$nextTick(this.renderCharts);
    },

    setMetric(key) {
      if (this.activeMetric === key) return;
      this.activeMetric = key;
      this.disposeCharts();
      this.$nextTick(this.renderCharts);
    },

    openTerm(term) {
      bus.$emit("cm:term", term);
    },

    /** Оставляет на графике только выбранный ряд. */
    isolate(term) {
      this.hidden = new Set(this.terms.filter((t) => t.term !== term).map((t) => t.term));
      this.renderCharts();
    },

    toggle(term) {
      // Set не реактивен во Vue 2 — пересобираем, чтобы перерисовать легенду
      const next = new Set(this.hidden);
      if (next.has(term)) next.delete(term);
      else next.add(term);
      this.hidden = next;
      this.renderCharts();
    },

    load() {
      // silent: оставляем старые графики на экране, пока не придёт новый ответ —
      // иначе loading прячет DOM, а echarts остаётся привязан к уничтоженным узлам
      return this.cmLoad(
        () => cmApi.fetchTimelines(this.cmScope, this.scope),
        (data) => {
          this.rawTerms = (data && data.terms) || [];
          // по умолчанию рисуем первые 5 линий — иначе график превращается в кашу
          this.hidden = new Set(this.terms.slice(5).map((t) => t.term));
          if (!this.terms.length) this.disposeCharts();
        },
        { silent: true },
      ).then((fresh) => {
        if (fresh && this.terms.length) this.$nextTick(this.renderCharts);
      });
    },

    seriesFor(key) {
      return this.terms
        .map((t, i) => ({ t, i }))
        .filter(({ t }) => !this.hidden.has(t.term))
        .map(({ t, i }) => {
          const color = this.colorOf(i);
          const rgb = echarts.color.parse(color).slice(0, 3).join(",");
          // раскладываем ряд по общей оси. Упоминаний в пропуске НОЛЬ — так и
          // рисуем, линия падает к нулю. У относительных метрик значения в
          // пропуске нет (поста не было) — оставляем разрыв, а одиночные точки
          // показываем символом, иначе их не видно вовсе.
          const byDate = new Map(t.series.map((p) => [p.date, p[key]]));
          const counts = key === "mentions";
          return {
            name: t.term,
            type: "line",
            smooth: 0.4,
            connectNulls: false,
            showSymbol: !counts,
            symbolSize: counts ? 6 : 4,
            lineStyle: { width: t.added ? 2 : 1, color },
            itemStyle: { color },
            emphasis: {
              focus: "series",
              symbol: "circle",
              itemStyle: {
                color,
                borderColor: `rgba(${rgb}, 0.3)`,
                borderWidth: 10,
                opacity: 1,
              },
            },
            data: this.axisDates.map((date) => {
              if (byDate.has(date)) return byDate.get(date);
              return counts ? 0 : null;
            }),
          };
        });
    },

    /** Тултип как в Dash-activity: прозрачный фон + разметка .tooltip/.tooltip-list. */
    tooltipFormatter(params, key) {
      if (!params.length) return "";
      const date = params[0].axisValueLabel || params[0].name;
      let html = `<div class="tooltip"><span class="date">${date}</span><ul class="tooltip-list">`;
      let hasData = false;
      params.forEach((p) => {
        const value = p.data;
        if (value == null) return;
        if (key === "mentions" && !value) return;   // нули — шум, слово не упоминалось
        hasData = true;
        html += `<li data-value-lines="${value}">
          <div class="soc-sea" style="color:${p.color}"><span class="cm-dot" style="background:${p.color};margin-right:6px"></span>${p.seriesName}</div>${Number(value).toLocaleString("ru-RU")}
        </li>`;
      });
      html += "</ul></div>";
      return hasData ? html : "";
    },

    chartOption(key) {
      return merge({}, comOpt, {
        grid: { top: 10, right: 8, bottom: 4, left: 4, containLabel: true },
        tooltip: {
          trigger: "axis",
          triggerOn: "mousemove|click",
          confine: true,
          axisPointer: {
            show: true,
            snap: true,
            label: { show: false },
            lineStyle: { color: "#C2D5FF", type: "line" },
          },
          formatter: (params) => this.tooltipFormatter(params, key),
        },
        xAxis: { type: "category", boundaryGap: false, data: this.dates },
        yAxis: { type: "value" },
        series: this.seriesFor(key),
      });
    },

    buildChart(ref, key) {
      if (!ref) return null;

      ref.style.touchAction = "manipulation";
      const chart = echarts.init(ref, null, { renderer: "canvas" });
      enableTouchTooltip(chart);
      // на desktop клик по линии открывает термин; на таче оставляем tip
      chart.on("click", (params) => {
        if (window.matchMedia && window.matchMedia("(hover: none)").matches) return;
        if (params && params.seriesName) bus.$emit("cm:term", params.seriesName);
      });
      return { chart, key };
    },

    /** Инициализируем инстансы один раз, дальше только обновляем через setOption. */
    renderCharts() {
      if (!this.terms.length) return;

      if (!this.charts.length) {
        this.charts = [
          this.buildChart(this.$refs.metricChart, this.activeMetric),
        ].filter(Boolean);
      }

      this.updateCharts();
    },

    /** Переключение периода/скрытие линий не пересоздаёт графики — только данные. */
    updateCharts() {
      this.charts.forEach(({ chart, key }) => {
        chart.setOption(this.chartOption(key), { notMerge: true });
        // ECharts кэширует размер контейнера с момента init — принудительно
        // перечитываем, иначе canvas остаётся 0, если при init блок был скрыт/0.
        chart.resize();
      });
    },

    disposeCharts() {
      this.charts.forEach(({ chart }) => chart.dispose());
      this.charts = [];
    },

    resizeCharts() {
      this.charts.forEach(({ chart }) => chart.resize());
    },
  },

  created() {
    // resize летит десятками событий за перетаскивание окна, а перерисовка
    // трёх графиков не бесплатная — дожидаемся паузы
    this.onResize = debounce(this.resizeCharts, 150);
  },

  mounted() {
    window.addEventListener("resize", this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.onResize.cancel();
    this.disposeCharts();
  },
};
</script>
